import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { Stethoscope, ArrowLeft, ArrowRight, Star, Award, MessageSquareQuote } from 'lucide-react';
import { BrandLogo } from '../components/BrandLogo';

export default function ClinicianProfile() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/clinicians/public/${slug}`)
      .then(r => {
        if (!alive) return;
        setC(r.data);
        return api.get(`/reviews/public?clinician_id=${r.data.user_id}&limit=50`);
      })
      .then(rv => { if (alive && rv) setReviews(rv.data || []); })
      .catch(e => { if (alive) setError(e.response?.data?.detail || 'Profile not found'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [slug]);

  // Inject Open Graph + Twitter Card metadata for share previews and browser tab.
  useEffect(() => {
    if (!c) return;
    const baseUrl = `${window.location.origin}/p/${c.slug}`;
    const title = `${c.name}${c.credentials ? ', ' + c.credentials : ''} — Physiogenic`;
    const desc = c.bio
      ? c.bio.slice(0, 180) + (c.bio.length > 180 ? '…' : '')
      : `Book a session with ${c.name} at Physiogenic — physiotherapy in Warsaw, Poland.`;
    const img = c.photo_url || `${window.location.origin}/logo.jpg`;
    const prev = document.title;
    document.title = title;
    const tags = [
      ['og:type', 'profile'],
      ['og:title', title], ['og:description', desc], ['og:image', img], ['og:url', baseUrl],
      ['twitter:card', 'summary_large_image'],
      ['twitter:title', title], ['twitter:description', desc], ['twitter:image', img],
      ['description', desc],
    ];
    const created = [];
    tags.forEach(([k, v]) => {
      const isProp = k.startsWith('og:');
      const sel = isProp ? `meta[property='${k}']` : `meta[name='${k}']`;
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement('meta');
        if (isProp) el.setAttribute('property', k); else el.setAttribute('name', k);
        document.head.appendChild(el);
        created.push(el);
      }
      el.setAttribute('content', v);
    });
    return () => {
      document.title = prev;
      created.forEach(el => el.remove());
    };
  }, [c]);

  const avg = reviews.length
    ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
    : null;

  if (loading) return <Shell><div className="p-12 text-center text-fg-muted">Loading…</div></Shell>;
  if (error || !c) return (
    <Shell>
      <div className="p-12 text-center max-w-md mx-auto">
        <div className="text-fg-muted text-sm mb-2">{error || 'Profile not found'}</div>
        <Link to="/" className="btn-secondary text-sm inline-flex items-center gap-1.5" data-testid="back-home">
          <ArrowLeft className="w-4 h-4"/> Back to Physiogenic
        </Link>
      </div>
    </Shell>
  );

  return (
    <Shell>
      <article className="max-w-4xl mx-auto px-6 py-10 md:py-14" data-testid="clinician-profile">
        <Link to="/" className="text-xs text-fg-muted inline-flex items-center gap-1 mb-6 hover:text-brand" data-testid="back-link">
          <ArrowLeft className="w-3.5 h-3.5"/> Physiogenic
        </Link>

        <div className="grid md:grid-cols-12 gap-8 items-start">
          <div className="md:col-span-4">
            <div className="aspect-square rounded-2xl overflow-hidden border border-soft surface">
              {c.photo_url ? (
                <img src={c.photo_url} alt={c.name} className="w-full h-full object-cover" data-testid="clinician-photo"/>
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-[#F2EBDB]">
                  <Stethoscope className="w-16 h-16 text-brand/40"/>
                </div>
              )}
            </div>
            {avg && (
              <div className="mt-4 surface border border-soft rounded-xl p-4 flex items-center gap-3" data-testid="avg-rating-card">
                <div className="text-3xl font-heading font-bold text-brand">{avg}</div>
                <div>
                  <StarRow rating={Math.round(parseFloat(avg))} size={4}/>
                  <div className="text-xs text-fg-muted mt-1">{reviews.length} verified review{reviews.length === 1 ? '' : 's'}</div>
                </div>
              </div>
            )}
          </div>

          <div className="md:col-span-8">
            <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">PHYSIOTHERAPIST</div>
            <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight" data-testid="clinician-name">{c.name}</h1>
            {c.credentials && (
              <div className="mt-3 inline-flex items-center gap-1.5 badge-success" data-testid="clinician-credentials">
                <Award className="w-3.5 h-3.5"/> {c.credentials}
              </div>
            )}
            {c.bio && (
              <p className="mt-5 text-fg-secondary leading-relaxed whitespace-pre-wrap" data-testid="clinician-bio">{c.bio}</p>
            )}
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to={`/login?next=/patient/book%3Fclinician%3D${c.user_id}`}
                    className="btn-primary inline-flex items-center gap-2"
                    data-testid="book-clinician-cta">
                Book with {c.name.split(' ')[0]} <ArrowRight className="w-4 h-4"/>
              </Link>
              <Link to="/clinicians" className="btn-secondary text-sm">All clinicians</Link>
            </div>
          </div>
        </div>

        <section className="mt-14 pt-10 border-t border-soft" data-testid="clinician-reviews-section">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-accent font-medium mb-2">PATIENT REVIEWS</div>
              <h2 className="text-2xl md:text-3xl font-heading font-bold tracking-tight">What patients say</h2>
            </div>
          </div>
          {reviews.length === 0 ? (
            <div className="bento-card text-center py-12" data-testid="no-reviews">
              <MessageSquareQuote className="w-8 h-8 text-fg-muted mx-auto mb-2"/>
              <div className="text-sm text-fg-muted">No reviews yet for {c.name.split(' ')[0]}. Be the first after your visit.</div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {reviews.map(r => <ReviewCard key={r.review_id} review={r}/>)}
            </div>
          )}
        </section>
      </article>
    </Shell>
  );
}

function ReviewCard({ review }) {
  return (
    <div className="bento-card" data-testid={`pub-review-${review.review_id}`}>
      <div className="flex items-center justify-between gap-2">
        <StarRow rating={review.rating}/>
        <SmallReviewBadge review={review}/>
      </div>
      <p className="mt-3 text-sm text-fg-secondary leading-relaxed whitespace-pre-wrap">"{review.text}"</p>
      <div className="mt-4 pt-3 border-t border-soft flex items-center justify-between text-xs text-fg-muted">
        <span className="font-medium text-fg">{review.author_name}</span>
        <span>{new Date(review.created_at).toLocaleDateString()}</span>
      </div>
    </div>
  );
}

function SmallReviewBadge({ review }) {
  if (review.is_backfilled) {
    const src = ({ google:'Google', facebook:'Facebook', paper:'Paper', word_of_mouth:'WoM', other:'External' })[review.source] || 'Imported';
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-[#FFF4D6] text-[#8A5A00] border border-[#FFB000]/40" title="Historical review imported from external platform" data-testid="review-badge-imported">
        <Star className="w-2.5 h-2.5 fill-[#FFB000] text-[#FFB000]"/> Imported · {src}
      </span>
    );
  }
  if (review.appointment_id) {
    return (
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded-full bg-[#D4F0DC] text-[#3A664D] border border-[#3A664D]/30" title="Tied to a verified appointment" data-testid="review-badge-verified">
        Verified visit
      </span>
    );
  }
  return null;
}

export function StarRow({ rating, size = 4 }) {
  const cls = size === 5 ? 'w-5 h-5' : size === 6 ? 'w-6 h-6' : 'w-4 h-4';
  return (
    <span className="inline-flex items-center gap-0.5">
      {[1,2,3,4,5].map(n => (
        <Star key={n} className={`${cls} ${n <= rating ? 'fill-[#FFB000] text-[#FFB000]' : 'text-[#E8DFC8]'}`}/>
      ))}
    </span>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen section">
      <header className="surface border-b">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="profile-brand-logo">
            <BrandLogo size={68}/>
            <span className="font-heading font-bold tracking-tight">Physiogenic</span>
          </Link>
          <Link to="/register" className="btn-primary text-sm">Book a session</Link>
        </div>
      </header>
      {children}
    </div>
  );
}
